import type { LitNodeClient } from '@lit-protocol/lit-node-client'
import { initializeKeyHierarchyQuery } from '@polaris/core/keys'
import type { InitializedKeys, SessionKeyData } from '@polaris/core/models'
import { getVaultSession } from '@polaris/core/mpc'
import { featureFlagsAtom } from '@polaris/core/stores'
import { mpcKeyHierarchyAtom } from '@polaris/core/stores/mpc/atom'
import { debug } from '@polaris/core/utils'
import { ensureKeyHierarchy as ensureKeyHierarchyClientSide } from '@polaris/lit-actions/src/lib/wrapped-keys/key-hierarchy'
import { useQueryClient } from '@tanstack/react-query'
import { useAtom, useAtomValue } from 'jotai'
import { RESET } from 'jotai/utils'
import { useCallback, useMemo, useRef } from 'react'
import { log } from '@/src/services/logger'
import { useLitClient } from './use-lit-client'

export function useKeyHierarchy() {
  const { litClient, connect } = useLitClient()
  const [keyHierarchy, setKeyHierarchy] = useAtom(mpcKeyHierarchyAtom)
  const featureFlags = useAtomValue(featureFlagsAtom)
  const localKeyHierarchyPromise = useRef<Promise<InitializedKeys> | null>(null)
  const queryClient = useQueryClient()

  /**
   * Ensure the key hierarchy exists remotely using the query client.
   */
  const ensureKeyHierarchyRemote = useCallback(
    async (
      _litClient: LitNodeClient,
      sessionKeyData: SessionKeyData,
      _guestMode: boolean,
    ) => {
      debug({
        message: 'Using remote key hierarchy logic',
      })
      const result = await queryClient.fetchQuery(
        initializeKeyHierarchyQuery({ sessionKeyData }),
      )
      log({
        message: 'Key hierarchy ensured remote',
        context: {
          rootKeyId: result?.rootKeyId,
          runnerSecretId: result?.runnerSecretId,
          namadaKeyId: result?.namadaKeyId,
        },
      })
      return result
    },
    [queryClient],
  )

  /**
   * Ensure the key hierarchy exists using the client side key hierarchy logic.
   */
  const ensureKeyHierarchyLocal = useCallback(
    async (
      litClient: LitNodeClient,
      sessionKeyData: SessionKeyData,
      guestMode: boolean,
    ) => {
      debug({
        message: 'Using local key hierarchy logic',
      })

      // If a key hierarchy is already being ensured, return the existing promise
      if (localKeyHierarchyPromise.current) {
        log({
          message:
            'Key hierarchy already being ensured, returning existing promise',
          context: {},
        })
        const result = await localKeyHierarchyPromise.current
        log({
          message: 'Key hierarchy ensured client side using existing promise',
          context: {
            rootKeyId: result?.rootKeyId,
            runnerSecretId: result?.runnerSecretId,
            namadaKeyId: result?.namadaKeyId,
          },
        })
        return result
      }

      // Get the vault session signatures
      const { sessionSigs } = await getVaultSession(litClient, sessionKeyData)

      log({
        message: 'Ensuring key hierarchy client side',
        context: {
          sessionSigs,
          guestMode,
        },
      })

      // Set the promise to prevent multiple concurrent calls
      const keyHierarchyPromise = ensureKeyHierarchyClientSide(
        litClient,
        sessionSigs,
        guestMode,
      )
      localKeyHierarchyPromise.current = keyHierarchyPromise

      // Clear the promise when complete (handles both success and error cases)
      keyHierarchyPromise.finally(() => {
        localKeyHierarchyPromise.current = null
      })

      const result = await keyHierarchyPromise

      log({
        message: 'Key hierarchy ensured client side',
        context: {
          rootKeyId: result?.rootKeyId,
          runnerSecretId: result?.runnerSecretId,
          namadaKeyId: result?.namadaKeyId,
        },
      })

      return result
    },
    [],
  )

  /**
   * Ensure the key hierarchy based on the feature flag.
   */
  const ensureKeyHierarchyInternal = useMemo(
    () =>
      featureFlags.localKeyHierarchy
        ? ensureKeyHierarchyLocal
        : ensureKeyHierarchyRemote,
    [
      featureFlags.localKeyHierarchy,
      ensureKeyHierarchyRemote,
      ensureKeyHierarchyLocal,
    ],
  )

  const ensureKeyHierarchy = useCallback(
    // TODO: We can probably handle migration in here?
    async ({
      vaultSessionKeyData,
      guestMode = false,
    }: {
      vaultSessionKeyData: SessionKeyData
      guestMode?: boolean
    }) => {
      // Connect to the Lit client
      await connect()

      // Clear the key hierarchy
      setKeyHierarchy(RESET)

      // Ensure the key hierarchy exists
      const keyHierarchy = await ensureKeyHierarchyInternal(
        litClient,
        vaultSessionKeyData,
        guestMode,
      )

      // Store the key hierarchy
      setKeyHierarchy(keyHierarchy)

      return keyHierarchy
    },
    [connect, litClient, setKeyHierarchy, ensureKeyHierarchyInternal],
  )

  return {
    ensureKeyHierarchy,
    keyHierarchy,
  }
}
